import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getErrorMessage } from "@/lib/utils";
import { motion } from "framer-motion";
import { Snowflake, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import MapLocationPicker from "@/components/MapLocationPicker";
import SavedCardsSelector from "@/components/SavedCardsSelector";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";



const SnowPlowingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState("new");
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_phone: user.phone || prev.customer_phone,
        customer_email: user.email || prev.customer_email,
      }));
    }
  }, [user]);

  const [formData, setFormData] = useState({
    service_address: "",
    service_type: "",
    property_type: "",
    area_size: "",
    notes: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const serviceTypes = [
    { value: "driveway", label: "Driveway", desc: "Area size price only" },
    { value: "walkway", label: "Walkways & Sidewalks", desc: "+$20" },
    { value: "parking_lot", label: "Parking Lot", desc: "+$30" },
    { value: "full_property", label: "Full Property", desc: "+$40" },
  ];

  const getEstimate = () => {
    if (!formData.service_address || !formData.service_type || !formData.area_size) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!pickupCoords) {
      toast.error("Please select a valid location on the map");
      return;
    }
    setStep(2);
  };

  const estimatedPrice = (() => {
    const areaPrices = { small: 50, medium: 90, large: 130 };
    const serviceAddon = { driveway: 0, walkway: 20, parking_lot: 30, full_property: 40 };
    const base = areaPrices[formData.area_size] || 90;
    const addon = serviceAddon[formData.service_type] || 0;
    return base + addon;
  })();

  const proceedToPayment = async () => {
    setPaymentLoading(true);
    try {
      const response = await axios.post(`${API}/snow-plowing/booking`, { ...formData, pickup_coords: pickupCoords });
      const booking = response.data;
      const amount = booking.estimated_price || estimatedPrice;

      const serviceData = {
        booking_id: booking.id, booking_type: "snow_plowing", amount,
        customer_email: formData.customer_email, customer_name: formData.customer_name,
        service_type: "snow_plowing", pickup_address: formData.service_address, pickup_coords: pickupCoords,
      };

      if (selectedCardId && selectedCardId !== "new") {
        const authResponse = await axios.post(`${API}/payments/authorize-hold`, { ...serviceData, payment_method_id: selectedCardId }, { withCredentials: true });
        if (authResponse.data.success) {
          toast.success("Payment authorized! Finding your professional...");
          navigate("/matching", { state: { booking_id: booking.id, booking_type: "snow_plowing", service_type: "snow_plowing", pickup_lat: pickupCoords.latitude, pickup_lng: pickupCoords.longitude, pickup_address: formData.service_address } });
        } else { toast.error("Payment authorization failed."); }
      } else {
        const paymentResponse = await axios.post(`${API}/payments/create-checkout`, { ...serviceData, origin_url: window.location.origin });
        window.location.href = paymentResponse.data.checkout_url;
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Payment failed. Please try again."));
    } finally { setPaymentLoading(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="snow-plowing-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-950 to-sky-900" />
        <div className="relative section-container text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 bg-sky-800/50 border border-sky-600/30 text-sky-200 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Snowflake className="w-4 h-4" /> Snow Plowing Services
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Snow Plowing <span className="text-sky-300">On Demand</span>
            </h1>
            <p className="text-base text-sky-200 max-w-xl mx-auto">
              Get your driveway, walkways, and property cleared fast. Professional snow removal at your doorstep.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-container py-12">
        <div className="max-w-3xl mx-auto">
          {step === 1 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="glass-card p-8" data-testid="snow-step-1">
            <h2 className="heading-3 text-emerald-950 mb-8">Service Details</h2>

            <div className="space-y-6">
              {/* Map */}
              <MapLocationPicker
                pickupAddress={formData.service_address}
                onPickupChange={(val) => handleInputChange("service_address", val)}
                onPickupCoordsChange={setPickupCoords}
                pickupLabel="Property Address *"
                pickupPlaceholder="Enter your address"
                singleLocation={true}
                serviceType="snow_plowing"
              />

              {/* Service Type */}
              <div>
                <Label className="text-emerald-950 font-medium mb-3 block">Service Type *</Label>
                <div className="grid grid-cols-2 gap-3">
                  {serviceTypes.map((svc) => (
                    <button key={svc.value} type="button" onClick={() => handleInputChange("service_type", svc.value)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${formData.service_type === svc.value ? "border-sky-500 bg-sky-50" : "border-stone-200 hover:border-sky-300"}`}
                      data-testid={`service-${svc.value}`}
                    >
                      <p className="font-medium text-emerald-950 text-sm">{svc.label}</p>
                      <p className="text-xs text-stone-500 mt-0.5">{svc.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Property Type */}
              <div>
                <Label className="text-emerald-950 font-medium mb-2 block">Property Type</Label>
                <Select value={formData.property_type} onValueChange={(v) => handleInputChange("property_type", v)}>
                  <SelectTrigger className="input-style" data-testid="property-type-select">
                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="condo">Condo / Townhouse</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Area Size */}
              <div>
                <Label className="text-emerald-950 font-medium mb-2 block">Area Size *</Label>
                <Select value={formData.area_size} onValueChange={(v) => handleInputChange("area_size", v)}>
                  <SelectTrigger className="input-style" data-testid="area-size-select">
                    <SelectValue placeholder="Select area size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small (single car driveway) — $50</SelectItem>
                    <SelectItem value="medium">Medium (double driveway) — $90</SelectItem>
                    <SelectItem value="large">Large (commercial / multi-car) — $130</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-emerald-950 font-medium mb-2 block">Additional Notes</Label>
                <Textarea className="input-style min-h-[80px]" placeholder="Gate codes, obstacles, salt/sand preferences..." value={formData.notes} onChange={(e) => handleInputChange("notes", e.target.value)} data-testid="notes-input" />
              </div>

              <Button className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-full py-6 font-bold text-lg" onClick={getEstimate} disabled={loading} data-testid="continue-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <>Continue <ArrowRight className="w-5 h-5 ml-2" /></>}
              </Button>
            </div>
          </motion.div>
          )}

          {/* Step 2: Payment */}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8 mt-6" data-testid="payment-step">
              <h2 className="text-xl font-bold text-emerald-950 mb-4">Payment</h2>
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-stone-600">Estimated Price</p>
                <p className="text-3xl font-bold text-sky-700">${estimatedPrice.toFixed(2)} <span className="text-sm font-normal text-stone-500">CAD</span></p>
              </div>
              <SavedCardsSelector onSelect={setSelectedCardId} selectedCardId={selectedCardId} />
              <Button className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-full py-6 font-bold mt-4" onClick={proceedToPayment} disabled={paymentLoading} data-testid="pay-btn">
                {paymentLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <>Pay & Find Professional <ArrowRight className="w-5 h-5 ml-2" /></>}
              </Button>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SnowPlowingPage;
